import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { getUserProfilePictureOptions } from "@/lib/data/react-query";
import type { User } from "@/lib/types/Firm";
import { cn } from "@/lib/utils/cn";
import { formatUsersName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../shadcn/Avatar/Avatar";

function getInitials(name?: string): string {
  if (!name) return "";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export const UserProfilePhotoFallback = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => {
  return (
    <AvatarFallback className={cn("dark:bg-muted bg-blue-50", className)}>
      {text}
    </AvatarFallback>
  );
};

export const UserProfilePhoto = ({
  user,
  className,
  initialsClassName,
}: {
  user?: Pick<
    User,
    "profilePicturePresignedUrlKey" | "id" | "firstName" | "lastName"
  >;
  className?: string;
  initialsClassName?: string;
}) => {
  const { data: userProfilePicture, isLoading } = useQuery(
    getUserProfilePictureOptions(
      user?.profilePicturePresignedUrlKey ?? "",
      user?.id ?? "",
    ),
  );
  const name = formatUsersName(user);
  const initials = useMemo(() => getInitials(name ?? "Unknown user"), [name]);
  return (
    <Avatar className={cn("text-dark-primary h-8 w-8 rounded-full", className)}>
      {isLoading ? (
        <AvatarFallback>
          <LoadingSpinner />
        </AvatarFallback>
      ) : userProfilePicture?.url ? (
        <>
          <AvatarImage
            className="object-cover object-top"
            loading="eager"
            src={userProfilePicture.url}
          />
          <UserProfilePhotoFallback
            text={initials}
            className={initialsClassName}
          />
        </>
      ) : (
        <UserProfilePhotoFallback
          text={initials}
          className={initialsClassName}
        />
      )}
    </Avatar>
  );
};
